// Shared season-selector helpers (stats page + player profile modal).
// UI doctrine: seasons render as year pills ("2025/26"), ordered oldest ->
// newest left-to-right, newest/current rightmost and selected by default.

export interface SeasonOptionLike {
  season_identifier: string;
  is_current: boolean;
}

// 'EPL 2025/2026' -> '2025/26', 'La Liga 2026/2027' -> '2026/27', 'MLS 2025' -> '2025'.
// The competition context is always visible elsewhere, so pills carry years only.
export const seasonPillLabel = (identifier: string): string => {
  const dual = identifier.match(/(\d{4})\s*\/\s*(\d{4})/);
  if (dual) return `${dual[1]}/${dual[2].slice(2)}`;
  const single = identifier.match(/\d{4}/);
  return single ? single[0] : identifier;
};

// Oldest -> newest; the current season always lands last (rightmost pill)
// even if an identifier is missing a parseable year.
export const sortSeasonsChronologically = <T extends SeasonOptionLike>(options: T[]): T[] => {
  const yearOf = (o: T): number => {
    const m = (o.season_identifier || '').match(/\d{4}/);
    return m ? Number(m[0]) : 0;
  };
  return [...options].sort(
    (a, b) => (yearOf(a) - yearOf(b)) || (Number(a.is_current) - Number(b.is_current)),
  );
};

// One comp's slice of a (possibly multi-comp) season slot. Cross-league
// views pair "EPL 2025/2026" with "La Liga 2025/2026" into a single
// "2025/26" pill; each part remembers its own identifier for fetching.
export interface SeasonSlotPart {
  compId: number;
  compName: string;
  ident: string;
  is_current: boolean;
  has_data: boolean;
}

export interface PairedSeasonSlots {
  options: Array<{ season_identifier: string; is_current: boolean; has_data: boolean }>;
  partsByIdent: Record<string, SeasonSlotPart[]>;
}

// Pair season slots ACROSS comps by year label. The canonical identifier is
// the first-listed comp's (it drives the pill label and selection state).
export const pairSeasonSlotsAcrossComps = (
  comps: Array<{ id: number; name?: string | null; short_name?: string | null }>,
  lists: Array<Array<{ season_identifier: string; is_current?: boolean; has_data?: boolean }>>,
): PairedSeasonSlots => {
  const byLabel = new Map<string, { ident: string; is_current: boolean; has_data: boolean; parts: SeasonSlotPart[] }>();
  comps.forEach((comp, i) => {
    for (const option of lists[i] ?? []) {
      const label = seasonPillLabel(option.season_identifier);
      const slot = byLabel.get(label) ?? {
        ident: option.season_identifier, is_current: false, has_data: false, parts: [],
      };
      slot.is_current = slot.is_current || !!option.is_current;
      slot.has_data = slot.has_data || !!option.has_data;
      slot.parts.push({
        compId: comp.id, compName: comp.name || comp.short_name || '',
        ident: option.season_identifier,
        is_current: !!option.is_current, has_data: !!option.has_data,
      });
      byLabel.set(label, slot);
    }
  });
  const options = [...byLabel.values()].map((slot) => ({
    season_identifier: slot.ident, is_current: slot.is_current, has_data: slot.has_data,
  }));
  const partsByIdent: Record<string, SeasonSlotPart[]> = {};
  for (const slot of byLabel.values()) partsByIdent[slot.ident] = slot.parts;
  return { options, partsByIdent };
};
